//Handles contact list selection state
import { useState, useCallback } from "react"

interface Contact {
  _id: string
  [key: string]: any
}

interface PageState {
  page: number
  isAllChecked: boolean
  data: Contact[]
}

/**
 * Custom React hook to manage contact list selection state.
 *
 * Responsibilities:
 * - Tracking individually selected contacts
 * - Managing per-page "select all" state
 * - Handling selection and deselection across paginated data
 * - Supporting bulk selection and clearing of selected contacts
 *
 * This hook is designed for paginated contact lists where users
 * can select individual contacts or toggle selection for an entire page.
 *
 * @returns {UseContactSelectionReturn} useContactSelectionResult
 *
 * @returns {Contact[]} useContactSelectionResult.selectedContacts
 * List of currently selected contacts across all pages.
 *
 * @returns {PageState[]} useContactSelectionResult.pages
 * Selection state per page, including "select all" status and page data.
 *
 * @returns {(contactId: string, checked: boolean, allContacts: Contact[]) => void}
 * useContactSelectionResult.handleSelectContact
 * Handles selection or deselection of an individual contact.
 *
 * @returns {(pageNumber: number, pageData?: Contact[]) => void}
 * useContactSelectionResult.togglePageCheck
 * Toggles "select all" state for a given page and updates selected contacts accordingly.
 *
 * @returns {() => void} useContactSelectionResult.clearSelection
 * Clears all selected contacts and resets page selection state.
 *
 * @returns {React.Dispatch<React.SetStateAction<Contact[]>>}
 * useContactSelectionResult.setSelectedContacts
 * Setter to manually update selected contacts if needed.
 */

interface UseContactSelectionReturn {
  selectedContacts: Contact[]
  pages: PageState[]
  handleSelectContact: (contactId: string, checked: boolean, allContacts: Contact[]) => void
  togglePageCheck: (pageNumber: number, pageData?: Contact[]) => void
  clearSelection: () => void
  setSelectedContacts: React.Dispatch<React.SetStateAction<Contact[]>>
}

export function useContactSelection(): UseContactSelectionReturn {
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([])
  const [pages, setPages] = useState<PageState[]>([
    { page: 1, isAllChecked: false, data: [] },
  ])

  const handleSelectContact = useCallback((
    contactId: string,
    checked: boolean,
    allContacts: Contact[]
  ) => {
    if (checked) {
      const contact = allContacts.find(c => c._id === contactId)
      if (contact) {
        setSelectedContacts(prev => [...prev, contact])
      }
    } else {
      setSelectedContacts(prev => prev.filter(c => c._id !== contactId))
    }
  }, [])

  const togglePageCheck = useCallback((pageNumber: number, pageData: Contact[] = []) => {
    setPages(prev => {
      const idx = prev.findIndex(p => p.page === pageNumber)
      let next = prev.slice()
      let toggled: PageState

      if (idx >= 0) {
        const existing = prev[idx]
        const mergedData = existing.data?.length ? existing.data : pageData
        toggled = {
          ...existing,
          data: mergedData,
          isAllChecked: !existing.isAllChecked,
        }
        next[idx] = toggled
      } else {
        toggled = { page: pageNumber, isAllChecked: true, data: pageData }
        next.push(toggled)
      }

      setSelectedContacts(prevSel => {
        if (toggled.isAllChecked) {
          const prevIds = new Set(prevSel.map(c => c._id))
          const toAdd = toggled.data.filter(c => !prevIds.has(c._id) && c.isActive !== false)
          return prevSel.concat(toAdd)
        } else {
          const pageIds = new Set(toggled.data.map(c => c._id))
          return prevSel.filter(c => !pageIds.has(c._id))
        }
      })

      return next
    })
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedContacts([])
    setPages([{ page: 1, isAllChecked: false, data: [] }])
  }, [])

  return {
    selectedContacts,
    pages,
    handleSelectContact,
    togglePageCheck,
    clearSelection,
    setSelectedContacts,
  }
}